import { GetListParams, GetOneParams } from 'react-admin';
import { CustomDataProvider, registerModuleDataProvider } from '../../providers/CustomDataProvider';
import { baseUrl, httpClient } from '../../providers/AppDataProvider';
import { Resources } from '../resource';

const PROXIES = Resources.proxyManagerProxies;
const POOLS = Resources.proxyManagerPools;
const PROXY_SCORES = Resources.proxyManagerProxyScores;

const PROXY_FILTER_KEYS = [
  'host',
  'port',
  'tier',
  'protocol',
  'countryCode',
  'ispName',
  'source',
  'isActive',
  'tags',
];
const POOL_FILTER_KEYS = ['isActive', 'syncMode'];

// Cursor store per resource: queryKey -> (page -> after cursor)
const cursorStores: Record<string, Map<string, Map<number, string | undefined>>> = {
  [PROXIES]: new Map(),
  [POOLS]: new Map(),
};

const getFilterKeys = (resource: string) =>
  resource === POOLS ? POOL_FILTER_KEYS : PROXY_FILTER_KEYS;

const buildQueryParams = (
  resource: string,
  filter: Record<string, any>,
  perPage: number,
  cursor?: string
) => {
  const queryParams = new URLSearchParams();

  getFilterKeys(resource).forEach((key) => {
    const value = filter[key];
    if (value === undefined || value === null || value === '') return;
    queryParams.set(key, String(value));
  });

  queryParams.set('first', String(perPage));

  if (cursor) {
    queryParams.set('after', cursor);
  }

  return queryParams;
};

const getQueryKey = (params: GetListParams) =>
  JSON.stringify({
    filter: params.filter ?? {},
    perPage: params.pagination?.perPage ?? 20,
  });

export const resetProxyManagerCursorStore = (resource?: string) => {
  if (resource) {
    cursorStores[resource]?.clear();
    return;
  }
  Object.values(cursorStores).forEach((store) => store.clear());
};

class ProxyManagerDataProvider implements CustomDataProvider {
  // eslint-disable-next-line class-methods-use-this
  async getList(resource: string, params: GetListParams): Promise<any> {
    if (resource !== PROXIES && resource !== POOLS) {
      return Promise.reject(new Error('Unsupported getList resource'));
    }

    const { page = 1, perPage = 20 } = params.pagination || {};
    const filter = (params.filter ?? {}) as Record<string, any>;

    const store = cursorStores[resource];
    const queryKey = getQueryKey(params);
    const pageCursors =
      store.get(queryKey) ?? new Map<number, string | undefined>([[1, undefined]]);
    store.set(queryKey, pageCursors);

    const cursor = page === 1 ? undefined : pageCursors.get(page);
    const queryParams = buildQueryParams(resource, filter, perPage, cursor);

    const url = `${baseUrl}/${resource}?${queryParams.toString()}`;
    const { json } = await httpClient(url, { method: 'GET' });

    const rows: any[] = json?.nodes ?? [];
    const nextCursor: string | undefined = json?.nextCursor ?? undefined;

    if (nextCursor) {
      pageCursors.set(page + 1, nextCursor);
    } else {
      pageCursors.delete(page + 1);
    }

    const data = rows.map((row: any, index: number) => ({
      id: row?.id ?? `${page}-${index}`,
      ...row,
    }));

    return {
      data,
      total: json?.totalCount ?? data.length,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  async getOne(resource: string, params: GetOneParams): Promise<any> {
    const url = `${baseUrl}/${resource}/${params.id}`;
    const { json } = await httpClient(url, { method: 'GET' });
    const payload = json?.data ?? json;
    return { data: { id: payload?.id ?? params.id, ...payload } };
  }

  // eslint-disable-next-line class-methods-use-this
  async create(resource: string, params: any): Promise<any> {
    const url = `${baseUrl}/${resource}`;
    const { json } = await httpClient(url, {
      method: 'POST',
      body: JSON.stringify(params.data),
    });
    const payload = json?.data ?? json;
    return { data: { id: payload?.id ?? `${Date.now()}`, ...payload } };
  }

  // eslint-disable-next-line class-methods-use-this
  async update(resource: string, params: any): Promise<any> {
    const url = `${baseUrl}/${resource}/${params.id}`;
    const { json } = await httpClient(url, {
      method: 'PATCH',
      body: JSON.stringify(params.data),
    });
    const payload = json?.data ?? json;
    return { data: { id: payload?.id ?? params.id, ...payload } };
  }

  // eslint-disable-next-line class-methods-use-this
  async delete(resource: string, params: any): Promise<any> {
    const url = `${baseUrl}/${resource}/${params.id}`;
    await httpClient(url, { method: 'DELETE' });
    return { data: { id: params.id, ...(params.previousData ?? {}) } };
  }
}

export const proxyManagerDataProvider = new ProxyManagerDataProvider();

registerModuleDataProvider(PROXIES, proxyManagerDataProvider);
registerModuleDataProvider(POOLS, proxyManagerDataProvider);

// Cursor store for proxy scores pagination
const proxyScoresCursorStore = new Map<string, Map<number, string | undefined>>();

export const resetProxyScoresCursorStore = () => proxyScoresCursorStore.clear();

const proxyScoresDataProvider: CustomDataProvider = {
  async getList(_resource: string, params: GetListParams): Promise<any> {
    const { page = 1, perPage = 20 } = params.pagination || {};
    const filter = (params.filter ?? {}) as Record<string, any>;
    const target = filter.target as string | undefined;

    const queryKey = JSON.stringify({ perPage, target });
    const pageCursors =
      proxyScoresCursorStore.get(queryKey) ??
      new Map<number, string | undefined>([[1, undefined]]);
    proxyScoresCursorStore.set(queryKey, pageCursors);

    const cursor = page === 1 ? undefined : pageCursors.get(page);
    const query = new URLSearchParams();
    query.set('first', String(perPage));
    if (cursor) query.set('after', cursor);
    if (target) query.set('target', target);

    const url = `${baseUrl}/proxy-manager/target-proxy-scores?${query.toString()}`;
    const { json } = await httpClient(url, { method: 'GET' });

    const rows: any[] = Array.isArray(json) ? json : (json?.items ?? []);
    const nextCursor: string | undefined = json?.nextCursor ?? undefined;

    if (nextCursor) {
      pageCursors.set(page + 1, nextCursor);
    } else {
      pageCursors.delete(page + 1);
    }

    const data = rows.map((row: any, index: number) => ({
      id: `${row.proxy?.id ?? index}-${row.targetKey ?? index}`,
      ...row,
    }));

    return {
      data,
      total: nextCursor ? (page * perPage) + 1 : (page - 1) * perPage + rows.length,
    };
  },
};

registerModuleDataProvider(PROXY_SCORES, proxyScoresDataProvider);
